import React, { useCallback, useEffect, useReducer, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import RequiredDocumentGeneralDataCard from './RequiredDocumentGeneralDataCard';
import { initialState, reducer } from 'features/requiredDocuments/reducers/generalData/containerReducer';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { requiredDocumentValidator } from 'features/requiredDocuments/validators';
import { Card, FakeText } from '@totalsoft/rocket-ui';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import useLanguageTracker from 'hooks/useLanguageTracker';
import { CONTENT_TYPES_QUERY } from 'features/documentTemplates/edit/queries';
import { ADD_UPDATE_REQUIRED_DOCUMENT } from '../../mutations';
import { REQUIRED_DOCUMENT_UPDATED } from '../../subscriptions';
import { REQUIRED_DOCUMENT_QUERY } from '../../queries';
import { useToast } from 'hooks/toasts/useToast';
import { uniq } from 'ramda';

const RequiredDocumentGeneralDataContainer = ({ id, onChangeRequiredDocumentTitle, onRedirect }) => {
  const addToast = useToast();
  const [t] = useTranslation();

  const [localState, dispatch] = useReducer(reducer, initialState);
  const [saving, setSaving] = useState(false);

  const [requiredDocumentValidation, validateRequiredDocument] = useDirtyFieldValidation(requiredDocumentValidator);

  const { data, loading, refetch } = useQuery(REQUIRED_DOCUMENT_QUERY, {
    variables: { id: id },
    skip: id === undefined || id === null
  });

  const { data: contentTypesData } = useQuery(CONTENT_TYPES_QUERY);

  const contentTypesOptions = useMemo(
    () => (contentTypesData ? uniq(contentTypesData.contentTypes.map(x => x.name)) : []),
    [contentTypesData]
  );

  const [addOrUpdateRequiredDocument] = useMutation(ADD_UPDATE_REQUIRED_DOCUMENT);

  useSubscription(REQUIRED_DOCUMENT_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('RequiredDocument.RequiredDocumentUpdated'), 'success');
      const requiredDocumentId = data.requiredDocumentUpdated.requiredDocument.requiredDocTypeId;
      dispatch({ type: 'OnHeaderSaved', requiredDocumentId });
      if (id === undefined) onRedirect(requiredDocumentId);
      setSaving(false);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedRequiredDocuments' });
      client.cache.gc();
    }
  });

  const handlePropertyChanged = (propertyName, value) => {
    dispatch({ type: 'OnHeaderPropertyChanged', propertyName, value });
  };

  const handleSave = () => {
    // eslint-disable-next-line no-unused-vars
    const { changed, ...reqDoc } = localState.requiredDocument;
    const input = {
      ...reqDoc
    };

    if (!validateRequiredDocument(input)) return;
    setSaving(true);
    return addOrUpdateRequiredDocument({ variables: { input } });
  };

  const onLanguageChange = useCallback(() => {
    refetch();
  }, [refetch]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  useEffect(() => {
    validateRequiredDocument(localState.requiredDocument);
  }, [localState.requiredDocument, validateRequiredDocument]);

  useEffect(() => {
    if (data) dispatch({ type: 'OnInitState', data });
  }, [data]);

  useEffect(() => {
    onChangeRequiredDocumentTitle(localState.requiredDocument.documentName);
  }, [localState.requiredDocument.documentName, onChangeRequiredDocumentTitle]);

  return (
    <>
      {loading ? (
        <Card>
          <FakeText lines={15} />
        </Card>
      ) : (
        <>
          <RequiredDocumentGeneralDataCard
            requiredDocument={localState.requiredDocument}
            contentTypes={contentTypesOptions}
            onSave={handleSave}
            onPropertyChanged={handlePropertyChanged}
            saving={saving}
            validation={requiredDocumentValidation}
            id={id}
          />
        </>
      )}
    </>
  );
};

RequiredDocumentGeneralDataContainer.propTypes = {
  id: PropTypes.number,
  onChangeRequiredDocumentTitle: PropTypes.func,
  onRedirect: PropTypes.func
};

export default RequiredDocumentGeneralDataContainer;
